import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FullscreenLoader } from "../components/FullscreenLoader";
import { useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import type { TranslationKey } from "../i18n";

const ERROR_REDIRECT_DELAY_MS = 2500;

const ERROR_CODES = [
  "UNAUTHENTICATED",
  "PERMISSION_DENIED",
  "NOT_FOUND",
  "INVALID_ARGUMENT",
  "INTERNAL",
] as const;

type ErrorCode = (typeof ERROR_CODES)[number];

const ERROR_KEYS: Record<ErrorCode, TranslationKey> = {
  UNAUTHENTICATED: "auth.callbackErrorUNAUTHENTICATED",
  PERMISSION_DENIED: "auth.callbackErrorPERMISSION_DENIED",
  NOT_FOUND: "auth.callbackErrorNOT_FOUND",
  INVALID_ARGUMENT: "auth.callbackErrorINVALID_ARGUMENT",
  INTERNAL: "auth.callbackErrorINTERNAL",
};

const isErrorCode = (value: string): value is ErrorCode =>
  (ERROR_CODES as readonly string[]).includes(value);

function parseFragment(hash: string): URLSearchParams {
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(trimmed);
}

export default function AuthCallbackPage() {
  const nav = useNavigate();
  const { applySession } = useAuth();
  const { t } = useI18n();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = parseFragment(window.location.hash);
    // Strip tokens / error info from the address bar before doing anything else.
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", cleanUrl);

    const scheduleReturn = (message: string) => {
      setErrorMessage(message);
      window.setTimeout(() => {
        nav("/", { replace: true });
      }, ERROR_REDIRECT_DELAY_MS);
    };

    const errorCode = params.get("error");
    if (errorCode) {
      const normalized = errorCode.toUpperCase();
      const headline = isErrorCode(normalized)
        ? t(ERROR_KEYS[normalized])
        : t("auth.callbackErrorUnknown");
      const description = params.get("error_description");
      scheduleReturn(description ? `${headline} ${description}` : headline);
      return;
    }

    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    if (!access_token || !refresh_token) {
      scheduleReturn(t("auth.callbackErrorMissingTokens"));
      return;
    }

    const expires_in_raw = params.get("expires_in");
    const expires_in = expires_in_raw ? Number(expires_in_raw) : undefined;
    const token_type = params.get("token_type") ?? "bearer";
    const auth_method = params.get("auth_method") ?? "social";
    const createdUser = params.get("created_user") === "1";
    // Reserved: MFA step-up isn't wired in the backend yet, but the flag is
    // already in the contract so we keep a branch ready for it.
    const requiresMfa = params.get("requires_mfa_step_up") === "1";

    applySession({
      access_token,
      refresh_token,
      expires_in,
      token_type,
      auth_method,
    })
      .then(() => {
        if (requiresMfa) {
          // Placeholder — route to a future MFA step-up flow.
          nav("/", { replace: true });
          return;
        }
        nav(createdUser ? "/onboarding" : "/overview", { replace: true });
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : t("auth.callbackErrorUnknown");
        scheduleReturn(message);
      });
  }, [applySession, nav, t]);

  const label = errorMessage ?? t("auth.callbackSigningIn");
  return <FullscreenLoader label={label} />;
}
